/**
 * SettingSyncService
 *
 * 범용 설정 동기화 서비스.
 * 서버 User Settings API와 key-value 설정을 GET/PUT으로 동기화한다.
 * ETag 기반 조건부 요청으로 불필요한 데이터 전송을 방지한다.
 *
 * 이 서비스는 설정의 의미를 모른다.
 * Feature 모듈이 addChangeListener 리스너를 통해 변경에 반응한다.
 *
 * 설정의 실제 값은 Redux(getDBSettings)에서 관리한다.
 * 이 서비스는 자체 캐시를 갖지 않으며, 서버 통신과 ETag만 관리한다.
 */

import settingSchema from './server-setting.schema.json'
import { SETTINGS_ALLOWED_KEYS } from 'redux/settings/settingMapping'
import RestClient from 'services/restClient'
import type { RestClientResponse } from 'services/restClient'

type SettingRecord = Record<string, unknown>

export class SchemaValidationError extends Error {
  constructor(detail: string) {
    super(`SettingSyncService: schema validation failed — ${detail}`)
    this.name = 'SchemaValidationError'
  }
}
type ChangeListener = (setting: SettingRecord) => void | Promise<void>
type SettingsProvider = () => SettingRecord
type SettingMigrator = (setting: SettingRecord) => Promise<void>
/**
 * 서버 수신값(마이그레이션 후, 로컬 머지 전)과 로컬 현재값을 비교해 "로컬 값을 서버에 다시 올려야 하는지" 판정한다.
 * 다른(구버전) 클라이언트가 full-replace PUT 으로 필드를 지운 경우를 복구하는 용도. 서비스는 의미를 모르고 앱이 주입한다.
 */
type ReuploadChecker = (serverSetting: SettingRecord, localSetting: SettingRecord) => boolean

interface ETagAccessor {
  load: () => Promise<string | null>
  save: (value: string | null) => Promise<void>
}

const deepSet = (obj: SettingRecord, path: string, value: unknown): SettingRecord => {
  const keys = path.split('.')
  if (keys.length === 1) return { ...obj, [keys[0]]: value }

  const result = { ...obj }
  let current: Record<string, unknown> = result
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]
    current[k] = { ...((current[k] as Record<string, unknown>) || {}) }
    current = current[k] as Record<string, unknown>
  }
  current[keys[keys.length - 1]] = value
  return result
}

const deepGet = (obj: SettingRecord, path: string): unknown => {
  const keys = path.split('.')
  let current: unknown = obj
  for (const k of keys) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[k]
  }
  return current
}

const MIN_SYNC_INTERVAL = 1000

// ─── JSON Schema 기반 검증 ─────────────────────────────────

/**
 * 스키마에서 각 카테고리의 허용 키와 타입 정보를 추출한다.
 */
interface PropertySchema {
  type?: string | string[]
  $ref?: string
}

const buildSchemaInfo = (): {
  categories: Set<string>
  categoryKeys: Record<string, Set<string>>
} => {
  const settingProps = (
    (settingSchema as Record<string, unknown>).properties as Record<
      string,
      { properties?: Record<string, unknown> }
    >
  ).setting.properties as Record<string, PropertySchema>

  const defs = (settingSchema as Record<string, unknown>).$defs as Record<
    string,
    { properties?: Record<string, unknown> }
  >

  const categories = new Set<string>()
  const categoryKeys: Record<string, Set<string>> = {}

  for (const [key, def] of Object.entries(settingProps)) {
    if (key === 'version') continue // version은 카테고리가 아님
    const refName = def.$ref?.replace('#/$defs/', '')
    if (refName && defs[refName]?.properties) {
      categories.add(key)
      categoryKeys[key] = new Set(Object.keys(defs[refName].properties))
    }
  }

  return { categories, categoryKeys }
}

const SCHEMA_INFO = buildSchemaInfo()

/**
 * setting 객체가 서버 스키마에 맞는지 검증한다.
 * 스키마에 정의된 모든 카테고리가 존재하고 올바른 타입이어야 한다.
 * @returns 검증 실패 시 에러 메시지, 성공 시 null
 */
const validateSetting = (setting: SettingRecord): string | null => {
  for (const category of SCHEMA_INFO.categories) {
    const value = setting[category]
    if (value == null) {
      return `카테고리 "${category}"가 존재하지 않습니다`
    }
    if (typeof value !== 'object' || Array.isArray(value)) {
      return `카테고리 "${category}"의 타입이 올바르지 않습니다 (expected: object, got: ${Array.isArray(value) ? 'array' : typeof value})`
    }
  }
  return null
}

/**
 * 서버 전송용 setting 객체를 준비한다.
 * localSettings를 제거하고 version을 포함시킨 후 스키마 검증을 수행한다.
 * @throws 검증 실패 시 Error (사용자에게 reload 안내)
 */
const prepareForSend = (setting: SettingRecord): SettingRecord => {
  const cleanSetting: SettingRecord = {}
  for (const [key, value] of Object.entries(setting)) {
    if (SETTINGS_ALLOWED_KEYS.has(key)) {
      cleanSetting[key] = value
    }
  }

  const error = validateSetting(cleanSetting)
  if (error != null) {
    // OMD-225: UI는 이 서비스(packages)에서 다루지 않는다. 사용자 고지는 앱 계층(UPLOAD_SETTINGS saga →
    // onSchemaError 핸들러 → 배너)에서 처리한다. 여기서는 진단 로그만 남기고 예외를 던진다.
    console.error('SettingSyncService: 설정 스키마 검증 실패 — 저장 중단', error)
    throw new SchemaValidationError(error)
  }

  return cleanSetting
}

// ─── 콜백 주입 (settingsMigration, settingsProvider) ───────

let getDBSettings: SettingsProvider | null = null
let migrateSettings: SettingMigrator | null = null
let reuploadChecker: ReuploadChecker | null = null

/**
 * 로컬 settings를 가져오는 콜백을 등록한다.
 * Redux state에서 현재 settings를 반환하는 함수.
 */
const setSettingsProvider = (provider: SettingsProvider): void => {
  getDBSettings = provider
}

/**
 * 서버 데이터 마이그레이션 콜백을 등록한다.
 * settingsMigration(setting, propertyAccessor)를 래핑하여 전달.
 */
const setSettingMigrator = (migrator: SettingMigrator): void => {
  migrateSettings = migrator
}

/**
 * 서버 수신 후 로컬 값 재업로드 판정 콜백을 등록한다(null 이면 해제).
 * 판정이 true 면 서버값 반영(notifyListeners)과 loaded 전이가 끝난 다음 tick 에 현재 로컬 설정을 1회 PUT 한다.
 */
const setReuploadChecker = (checker: ReuploadChecker | null): void => {
  reuploadChecker = checker
}

/**
 * 서버에서 수신한 setting을 마이그레이션 후 검증한다.
 * 검증 실패 시 로컬 값으로 초기화한다.
 *
 * NOTE: dbSetting은 getDBSettings() 콜백(apps/owma/src/myNet.ts L205-215)을 통해 로드되며,
 * 해당 콜백은 SETTINGS_ALLOWED_KEYS(store/settings/settingMapping.ts) 화이트리스트로 필터링한다.
 * 화이트리스트는 서버 동기화 대상 키(version/email/account/ai/calendar/todo/general)만 포함하며,
 * `localSettings`는 로컬 전용이므로 dbSetting에 절대 포함되지 않는다.
 * 따라서 아래 shallow merge가 DB의 localSettings를 덮어쓰는 시나리오는 발생하지 않는다.
 * (과거 리뷰에서 같은 오해가 반복된 바 있어 주석으로 명시)
 */
const validateAndMigrateServerSetting = async (
  setting: SettingRecord,
): Promise<{ merged: SettingRecord; needsReupload: boolean }> => {
  let dbSetting: SettingRecord = {}
  // 1. DB의 데이터 로딩 (SETTINGS_ALLOWED_KEYS로 필터링된 서버 sync 대상 키만 포함)
  if (getDBSettings != null) {
    dbSetting = { ...getDBSettings() }
  }

  // 2. 마이그레이션 시도 (settingsMigration은 setting 객체를 in-place mutation한다)
  if (migrateSettings != null) {
    try {
      await migrateSettings(setting)
    } catch {
      // 마이그레이션 실패 — 검증으로 진행
    }
  }

  // 재업로드 판정은 로컬과 머지하기 전의 서버 수신값으로 해야 한다(머지 후에는 로컬 값이 채워져 구분 불가).
  let needsReupload = false
  if (reuploadChecker != null) {
    try {
      needsReupload = reuploadChecker(setting, dbSetting)
    } catch (err) {
      console.error('SettingSyncService reuploadChecker error:', err)
    }
  }

  // 3. dbSetting(서버 sync 대상 키) + setting(마이그레이션 후 서버 수신값) 머지
  //    dbSetting에는 localSettings가 없으므로, setting 내 localSettings(v1→v2 변환 결과 등)가 있어도
  //    DB의 localSettings를 손상시키지 않는다. Redux 반영은 리스너의 deepMerge가 처리한다.
  let newSetting: SettingRecord = { ...dbSetting }

  for (const key of Object.keys(setting)) {
    const existing = newSetting[key] as Record<string, unknown> | undefined
    const incoming = setting[key] as Record<string, unknown> | undefined
    // 나중에 좀더 자세히 손보자.
    if (key === 'version') {
      newSetting[key] = incoming
    } else if (key === 'general' && incoming != null) {
      // locale 은 local-only 다(redux/settings/types.ts 의 localSettings.general.locale 로 이동).
      // 서버가 general.locale 을 보내더라도 무시해 redux general 로의 역유입을 차단한다.
      // dateFormat/timeFormat 등 나머지 general 필드만 반영한다.
      const incomingGeneral = { ...incoming }
      delete incomingGeneral.locale
      newSetting[key] = { ...(existing || {}), ...incomingGeneral }
    } else {
      newSetting[key] = { ...(existing || {}), ...(incoming || {}) }
    }
  }

  // 4. 검증
  const error = validateSetting(newSetting)
  if (error == null) return { merged: newSetting, needsReupload }

  // fallback 없이 그대로 사용 (최선)
  console.warn(`SettingSyncService: 서버 데이터 검증 실패 (${error}), fallback 없이 사용`)
  return { merged: dbSetting, needsReupload: false }
}

let restClient: RestClient | null = null

let etag: string | null = null
let etagLoaded = false
let initialized = false
let lastSyncAt = 0
const listeners = new Set<ChangeListener>()

// ─── 서버 로드 게이트 (OMD-225) ─────────────────────────────
// 서버 settings 로드가 성공적으로 끝나기(loaded) 전에는 어떤 서버 save(PUT)도 나가지 않도록 막는다.
// throttle 반환·네트워크 실패·hang 을 모두 "loaded 아님"으로 취급해 기본값이 서버를 덮어쓰는 것을 방지한다.
export type ServerLoadState = 'idle' | 'loading' | 'loaded' | 'failed'
type ServerLoadStateListener = (state: ServerLoadState) => void

// loading 이 이 시간을 넘겨 고착되면 failed 로 전이해 게이트가 세션을 영구 차단하지 않도록 한다.
const SERVER_LOAD_TIMEOUT_MS = 30000

let serverLoadState: ServerLoadState = 'idle'
// killswitch: 런타임 토글(원격 config/콘솔). false 면 게이트를 우회해 기존 동작으로 폴백한다.
let saveGuardEnabled = true
// loaded 이전에 drop 된 업로드가 있으면, loaded 전이 시 현재 Redux 기준으로 1회 reconciliation upload 한다.
let pendingReconciliation = false
let serverLoadTimeoutTimer: ReturnType<typeof setTimeout> | null = null
let reconciliationTimer: ReturnType<typeof setTimeout> | null = null
const serverLoadStateListeners = new Set<ServerLoadStateListener>()

const clearServerLoadTimeout = (): void => {
  if (serverLoadTimeoutTimer) {
    clearTimeout(serverLoadTimeoutTimer)
    serverLoadTimeoutTimer = null
  }
}

const notifyServerLoadStateListeners = (): void => {
  for (const cb of serverLoadStateListeners) {
    try {
      cb(serverLoadState)
    } catch (err) {
      console.error('SettingSyncService serverLoadStateListener error:', err)
    }
  }
}

/** 서버 로드 시작(loading) 표시 + stuck 방지 타임아웃 예약. 이미 loaded 면 유지(재-sync 실패로 게이트를 닫지 않는다). */
const markServerLoading = (): void => {
  if (serverLoadState === 'loaded') return
  serverLoadState = 'loading'
  clearServerLoadTimeout()
  serverLoadTimeoutTimer = setTimeout(() => {
    serverLoadTimeoutTimer = null
    if (serverLoadState !== 'loaded') {
      serverLoadState = 'failed'
      notifyServerLoadStateListeners()
    }
  }, SERVER_LOAD_TIMEOUT_MS)
  notifyServerLoadStateListeners()
}

/**
 * 현재 Redux 기준 전체 설정을 1회 PUT 하도록 예약한다(중복 예약은 하나로 합친다).
 * notifyListeners 로 Redux 에 서버값이 반영된 뒤 실행되도록 다음 tick 으로 미룬다. 타이머는 clear() 에서 정리된다.
 */
const scheduleReconciliation = (): void => {
  if (reconciliationTimer) clearTimeout(reconciliationTimer)
  reconciliationTimer = setTimeout(() => {
    reconciliationTimer = null
    void update({}).catch(() => {})
  }, 0)
}

/** 서버 로드 성공(loaded) 표시. 게이트를 열고, drop 된 업로드가 있었으면 1회 reconciliation upload 한다. */
const markServerLoaded = (): void => {
  clearServerLoadTimeout()
  const prev = serverLoadState
  serverLoadState = 'loaded'
  if (prev !== 'loaded') {
    notifyServerLoadStateListeners()
    if (pendingReconciliation) {
      pendingReconciliation = false
      scheduleReconciliation()
    }
  }
}

/** 서버 로드 실패(failed) 표시. 이미 loaded 면 유지(초기 로드만 게이트 대상). */
const markServerFailed = (): void => {
  clearServerLoadTimeout()
  if (serverLoadState === 'loaded') return
  if (serverLoadState === 'failed') return
  serverLoadState = 'failed'
  notifyServerLoadStateListeners()
}

const getServerLoadState = (): ServerLoadState => serverLoadState

const addServerLoadStateListener = (cb: ServerLoadStateListener): (() => void) => {
  serverLoadStateListeners.add(cb)
  return () => {
    serverLoadStateListeners.delete(cb)
  }
}

/** killswitch 토글. off 시 게이트를 우회한다(원격 config/긴급 콘솔용). */
const setSaveGuardEnabled = (value: boolean): void => {
  saveGuardEnabled = value
}

/**
 * 서버 save 게이트: loaded 이전(또는 실패/로딩 중)에는 업로드를 큐에 태우지 않고 drop 한다.
 * drop 시 pendingReconciliation 을 세워, loaded 전이 시 최신 Redux 로 1회 재전송하도록 한다.
 * @returns true 면 업로드를 진행해도 됨, false 면 drop(차단).
 */
const passServerSaveGate = (): boolean => {
  if (!saveGuardEnabled) return true
  if (serverLoadState === 'loaded') return true
  pendingReconciliation = true
  return false
}

let etagAccessor: ETagAccessor | null = null

const setETagAccessor = (accessor: ETagAccessor): void => {
  etagAccessor = accessor
}

const loadEtag = async (): Promise<void> => {
  if (etagLoaded) return
  etagLoaded = true
  try {
    etag = etagAccessor ? await etagAccessor.load() : null
  } catch {
    etag = null
  }
}

const saveEtag = async (newEtag: string | null): Promise<void> => {
  etag = newEtag
  try {
    if (etagAccessor) {
      await etagAccessor.save(newEtag)
    }
  } catch {
    // ignore storage errors
  }
}

const notifyListeners = async (data: SettingRecord): Promise<void> => {
  const snapshot = { ...data }
  // 서버에서 수신한 데이터를 로컬에 적용하는 동안 _applyingServerSettings 플래그를 true로 유지한다.
  // 콜백 안에서 dispatch되는 CHANGE_SETTING으로 인한 Redux 변경 → reload(fire-and-forget HTTP) →
  // 그 응답이 일으키는 추가 UPLOAD_SETTINGS 까지 echo로 차단하기 위해, 콜백 종료 후에도
  // 3초 동안 플래그를 유지한다 (HTTP 응답 윈도우 확보).
  setApplyingServerSettings(true)
  try {
    for (const cb of listeners) {
      try {
        await cb(snapshot)
      } catch (err) {
        console.error('SettingSyncService addChangeListener error:', err)
      }
    }
  } finally {
    _scheduleApplyingServerSettingsRelease(3000)
  }
}

/**
 * Redux에서 현재 settings를 읽어온다.
 * getDBSettings가 등록되지 않았으면 빈 객체를 반환한다.
 */
const getCurrentSettings = (): SettingRecord => {
  if (getDBSettings != null) {
    return { ...getDBSettings() }
  }
  return {}
}

const getRestClient = (): RestClient => {
  if (!restClient) {
    throw new Error('SettingSyncService: restClient not initialized. Call init() first.')
  }
  return restClient
}

/**
 * restClient 인스턴스를 주입한다.
 * 앱 초기화 시 myNet.ts의 restClient를 전달해야 한다.
 */
const init = (client: RestClient): void => {
  restClient = client
}

/**
 * 서버에서 설정을 가져온다.
 * ETag 기반 조건부 요청: 304면 변경 없음, 200이면 갱신 + 리스너 호출.
 * force=true면 throttle을 무시한다.
 */
const sync = async (force: boolean = false): Promise<void> => {
  const now = Date.now()

  // throttle 반환은 "성공 로드"가 아니다 — 상태를 건드리지 않고 반환해 게이트가 계속 닫혀 있게 한다(A-1).
  if (!force && now - lastSyncAt < MIN_SYNC_INTERVAL) return
  lastSyncAt = now

  // 진행 중인 업로드(PUT)가 끝난 뒤 GET 해야, 방금 저장한 값(메일 즐겨찾기 등)을 서버의 이전 값으로
  // 덮어쓰지 않는다. 미초기화 상태의 sync 는 update()/replaceAll() 큐 안에서 호출되므로 기다리면 교착된다.
  if (initialized) await updateQueue

  markServerLoading()

  await loadEtag()

  const client = getRestClient()
  const headers: Record<string, string> = {}
  if (etag) {
    headers['If-None-Match'] = etag
  }

  let response: RestClientResponse
  try {
    response = await client.get('/users/user-setting', undefined, { headers })
  } catch (error) {
    // 네트워크 에러(TypeError)나 타임아웃(AbortError)만 무시한다.
    // throttle(MIN_SYNC_INTERVAL=1초)이 유지되므로 자연스럽게 재시도된다.
    // HTTP 상태 에러(4xx/5xx)는 기존 동작 유지를 위해 재전파.
    if (
      error instanceof TypeError ||
      (error instanceof DOMException && error.name === 'AbortError')
    ) {
      // 네트워크/타임아웃 실패도 반드시 failed 로 표시해야 게이트·사용자 알림이 동작한다(A-2).
      markServerFailed()
      return
    }
    markServerFailed()
    throw error
  }

  // 304: 서버 데이터 변경 없음.
  if (response.origin.status === 304) {
    // 이미 초기화 완료 → 서버 변경 없으므로 할 일 없음
    if (initialized) {
      markServerLoaded()
      return
    }

    // 첫 sync — 먼저 DB에서 settings 로드 시도
    let dbLoaded = false
    if (getDBSettings != null) {
      try {
        const dbData = getDBSettings()
        if (dbData != null && Object.keys(dbData).length > 0) {
          const error = validateSetting(dbData)
          if (error == null) {
            initialized = true
            dbLoaded = true
            await notifyListeners(dbData)
            markServerLoaded()
          }
        }
      } catch {
        // DB 로드 실패 — 서버 재요청으로 진행
      }
    }

    // DB 로드 실패 또는 데이터 무효 → ETag 무효화 후 서버 재요청
    if (!dbLoaded) {
      await saveEtag(null)
      let retryResponse: RestClientResponse
      try {
        retryResponse = await client.get('/users/user-setting', undefined, {})
      } catch {
        lastSyncAt = 0
        markServerFailed()
        throw new Error('SettingSyncService: 304 retry failed')
      }
      if (retryResponse.origin.status === 200) {
        const retryData = retryResponse.data
        const setting: SettingRecord =
          retryData?.data?.setting ?? retryData?.setting ?? retryData ?? {}
        const { merged: migratedSetting, needsReupload } =
          await validateAndMigrateServerSetting(setting)

        initialized = true
        const newEtag =
          retryData?.data?.etag ??
          retryData?.etag ??
          retryResponse.origin.headers?.get?.('etag') ??
          null
        await saveEtag(newEtag)
        await notifyListeners(migratedSetting)
        markServerLoaded()
        if (needsReupload) scheduleReconciliation()
      } else {
        lastSyncAt = 0
        markServerFailed()
      }
    }
    return
  }

  const data = response.data
  const setting: SettingRecord = data?.data?.setting ?? data?.setting ?? data ?? {}
  const { merged: migratedSetting, needsReupload } = await validateAndMigrateServerSetting(setting)
  initialized = true

  const newEtag = data?.data?.etag ?? data?.etag ?? response.origin.headers?.get?.('etag') ?? null
  await saveEtag(newEtag)

  await notifyListeners(migratedSetting)
  markServerLoaded()
  // 다른(구버전) 클라이언트가 지운 필드를 로컬 값으로 복구한다. 반영·loaded 이후라 게이트를 통과한다.
  if (needsReupload) scheduleReconciliation()
}

/**
 * 하나 이상의 설정 키를 업데이트하고 서버에 PUT한다.
 * entries는 { 'category.key': value } 형태의 맵이다.
 * initialized가 false면 먼저 sync()를 호출하여 서버 상태를 확보한다.
 * 동시 호출 시 경쟁 조건을 방지하기 위해 직렬화한다.
 *
 * Redux에 이미 반영된 값을 getDBSettings()로 읽어 서버에 전송한다.
 */
let updateQueue: Promise<void> = Promise.resolve()

const update = (entries: Record<string, unknown>): Promise<void> => {
  // 서버 로드(loaded) 이전에는 업로드를 큐에 태우지 않고 drop 한다(OMD-225 게이트).
  // 기본값이 서버를 덮어쓰는 것을 막고, loaded 전이 시 1회 reconciliation 으로 최신 상태를 재전송한다.
  if (!passServerSaveGate()) {
    return Promise.resolve()
  }
  const task = updateQueue.then(async () => {
    if (!initialized) {
      await sync()
    }

    let current = getCurrentSettings()
    for (const [key, value] of Object.entries(entries)) {
      current = deepSet(current, key, value)
    }

    const payload = prepareForSend(current)

    const client = getRestClient()
    const response = await client.put('/users/user-setting', { setting: payload })

    const responseData = response.data
    const newEtag =
      responseData?.etag ??
      responseData?.data?.etag ??
      response.origin.headers?.get?.('etag') ??
      null
    if (newEtag) {
      await saveEtag(newEtag)
    }
  })
  updateQueue = task.then(
    () => {},
    () => {},
  )
  return task
}

/**
 * setting 전체를 새 객체로 교체하고 서버에 PUT한다.
 * 기존 데이터를 완전히 대체하므로, caller가 보존할 데이터(예: icsSubscriptions)를
 * 미리 새 객체에 포함해야 한다.
 * update()와 동일한 직렬화 큐를 사용하여 경쟁 조건을 방지한다.
 */
const replaceAll = (newSetting: SettingRecord): Promise<void> => {
  // update()와 동일하게 loaded 이전에는 drop 한다(OMD-225 게이트).
  if (!passServerSaveGate()) {
    return Promise.resolve()
  }
  const task = updateQueue.then(async () => {
    if (!initialized) {
      await sync()
    }

    const payload = prepareForSend(newSetting)

    const client = getRestClient()
    const response = await client.put('/users/user-setting', { setting: payload })

    const responseData = response.data
    const newEtag =
      responseData?.etag ??
      responseData?.data?.etag ??
      response.origin.headers?.get?.('etag') ??
      null
    if (newEtag) {
      await saveEtag(newEtag)
    }
  })
  updateQueue = task.then(
    () => {},
    () => {},
  )
  return task
}

/**
 * 현재 설정에서 특정 키의 값을 가져온다.
 * Redux state에서 읽는다.
 */
const getSetting = (key: string): unknown => {
  return deepGet(getCurrentSettings(), key)
}

/**
 * 설정 변경 리스너를 등록한다.
 * 반환값은 해제 함수.
 */
const addChangeListener = (callback: ChangeListener): (() => void) => {
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

/**
 * ETag 및 내부 상태를 초기화한다 (로그아웃 시 호출).
 * 서버 적용 중 플래그도 함께 리셋하여 재로그인 직후 잔여 플래그가 UPLOAD_SETTINGS를
 * 차단하지 않도록 보장한다.
 */
const clear = async (): Promise<void> => {
  etag = null
  etagLoaded = false
  initialized = false
  lastSyncAt = 0
  updateQueue = Promise.resolve()
  listeners.clear()
  setApplyingServerSettings(false)
  // 서버 로드 게이트 상태도 리셋 — 재로그인 직후 잔여 loaded/failed 가 저장을 잘못 허용/차단하지 않도록.
  clearServerLoadTimeout()
  if (reconciliationTimer) {
    clearTimeout(reconciliationTimer)
    reconciliationTimer = null
  }
  serverLoadState = 'idle'
  pendingReconciliation = false
  try {
    if (etagAccessor) {
      await etagAccessor.save(null)
    }
  } catch {
    // ignore
  }
}

/**
 * 서버에서 수신한 설정을 로컬에 적용하는 중인지 나타내는 플래그.
 * true인 동안에는 UPLOAD_SETTINGS를 건너뛰어 에코 업로드를 방지한다.
 *
 * 이 플래그는 `notifyListeners`(서버→로컬 적용 진입점)에서 자동으로 set/release된다.
 * 외부에서는 read 전용(`isApplyingServerSettings`)과 강제 해제(`cancelApplyingServerSettings`)만 노출한다.
 */
let _applyingServerSettings = false
let _applyingServerSettingsTimer: ReturnType<typeof setTimeout> | null = null

const setApplyingServerSettings = (value: boolean): void => {
  if (_applyingServerSettingsTimer) {
    clearTimeout(_applyingServerSettingsTimer)
    _applyingServerSettingsTimer = null
  }
  _applyingServerSettings = value
}

const isApplyingServerSettings = (): boolean => _applyingServerSettings

/**
 * notifyListeners 종료 후 호출되어, 지정된 시간 뒤 플래그를 자동 해제하는 타이머를 예약한다.
 * 콜백이 트리거한 reload 함수들의 후속 HTTP 응답이 도착할 시간을 확보하기 위함.
 * 내부 헬퍼이며 외부 export하지 않는다.
 */
const _scheduleApplyingServerSettingsRelease = (durationMs: number): void => {
  if (_applyingServerSettingsTimer) {
    clearTimeout(_applyingServerSettingsTimer)
  }
  _applyingServerSettingsTimer = setTimeout(() => {
    _applyingServerSettings = false
    _applyingServerSettingsTimer = null
  }, durationMs)
}

/**
 * 서버 설정 적용 중 플래그를 즉시 해제하고 타이머를 취소한다.
 * 로그아웃 등으로 동기화 상태를 리셋할 때 호출된다.
 */
const cancelApplyingServerSettings = (): void => {
  setApplyingServerSettings(false)
}

/** 테스트용: lastSyncAt을 리셋하여 throttle을 해제한다. */
const _resetSyncThrottle = (): void => {
  lastSyncAt = 0
}

/** 테스트용: 현재 서버 로드 게이트 상태를 반환한다. */
const _getServerLoadState = (): ServerLoadState => serverLoadState

const _deleteForTest = async (): Promise<RestClientResponse | null> => {
  if (__ENV_LOCAL__) {
    const client = getRestClient()
    const response = await client.put('/users/user-setting', { setting: {} })
    return response
  }

  return null
}

const settingSyncService = {
  init,
  sync,
  update,
  replaceAll,
  getSetting,
  addChangeListener,
  clear,
  setSettingsProvider,
  setSettingMigrator,
  setReuploadChecker,
  setETagAccessor,
  isApplyingServerSettings,
  cancelApplyingServerSettings,
  getServerLoadState,
  addServerLoadStateListener,
  setSaveGuardEnabled,
  _resetSyncThrottle,
  _getServerLoadState,
  _deleteForTest,
}

export default settingSyncService
